import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, setDoc, onSnapshot, deleteDoc, collection } from 'firebase/firestore';
import { UserList } from '../components/UserList';

const configuration: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]
};

const VideoChatScreen: React.FC = () => {
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [connectionState, setConnectionState] = useState<string>('disconnected');
  const [incomingCall, setIncomingCall] = useState<string | null>(null);
  const { user } = useAuth();
  const peerConnection = useRef<RTCPeerConnection | null>(null);
  const iceCandidatesQueue = useRef<RTCIceCandidate[]>([]);

  useEffect(() => {
    console.log('VideoChatScreen mounted');
    console.log('Current user ID:', user.uid);
    startLocalStream();
    listenForIncomingCalls();

    return () => {
      console.log('VideoChatScreen unmounting');
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
      }
      if (peerConnection.current) {
        peerConnection.current.close();
      }
    };
  }, [user.uid]);

  const startLocalStream = async () => {
    try {
      console.log('Starting local stream');
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setLocalStream(stream);
      console.log('Local stream started successfully');
    } catch (error) {
      console.error('Error accessing media devices:', error);
    }
  };

  const createPeerConnection = () => {
    console.log('Creating peer connection');
    peerConnection.current = new RTCPeerConnection(configuration);
    
    peerConnection.current.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('New ICE candidate:', event.candidate);
        setDoc(doc(collection(db, `calls/${user.uid}/ice-candidates`)), event.candidate.toJSON());
      }
    };

    peerConnection.current.onconnectionstatechange = () => {
      console.log('Connection state changed:', peerConnection.current!.connectionState);
      setConnectionState(peerConnection.current!.connectionState);
    };

    peerConnection.current.ontrack = (event) => {
      console.log('Received remote track');
      setRemoteStream(event.streams[0]);
    };

    localStream?.getTracks().forEach((track) => {
      console.log('Adding local track to peer connection');
      if (localStream) peerConnection.current?.addTrack(track, localStream);
    });
  };

  const handleIncomingICECandidates = (otherUserId: string) => {
    console.log('Listening for ICE candidates from:', otherUserId);
    onSnapshot(collection(db, `calls/${otherUserId}/ice-candidates`), (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const candidate = change.doc.data();
          console.log('Received ICE candidate:', candidate);
          
          if (peerConnection.current?.remoteDescription) {
            peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate))
              .catch(e => console.error('Error adding ICE candidate:', e));
          } else {
            iceCandidatesQueue.current.push(new RTCIceCandidate(candidate));
          }
        }
      });
    });
  };

  const startCall = async (calleeId: string) => {
    console.log('Starting call to:', calleeId);
    createPeerConnection();
    try {
      const offer = await peerConnection.current!.createOffer();
      console.log('Offer created:', offer);
      await peerConnection.current!.setLocalDescription(offer);
      console.log('Local description set');
      
      console.log('Sending offer to callee:', calleeId);
      await setDoc(doc(db, `calls/${calleeId}`), { 
        offer: { type: offer.type, sdp: offer.sdp },
        callerId: user.uid 
      });
      console.log('Offer sent to callee');

      handleIncomingICECandidates(calleeId);

      console.log('Listening for answer from:', calleeId);
      onSnapshot(doc(db, `calls/${user.uid}`), async (snapshot) => {
        const data = snapshot.data();
        if (data?.answer && !peerConnection.current!.currentRemoteDescription) {
          console.log('Received answer:', data.answer);
          try {
            await peerConnection.current!.setRemoteDescription(new RTCSessionDescription(data.answer));
            console.log('Remote description set');
            
            // Process queued candidates after remote description is set
            while (iceCandidatesQueue.current.length) {
              const candidate = iceCandidatesQueue.current.shift()!;
              await peerConnection.current!.addIceCandidate(candidate)
                .catch(e => console.error('Error adding queued ICE candidate:', e));
            }
          } catch (error) {
            console.error('Error setting remote description:', error);
          }
        }
      });
    } catch (error) {
      console.error('Error starting call:', error);
    }
  };

  const joinCall = async (callerId: string) => {
    console.log('Joining call from:', callerId);
    createPeerConnection();
    handleIncomingICECandidates(callerId);

    onSnapshot(doc(db, `calls/${user.uid}`), async (snapshot) => {
      const data = snapshot.data();
      console.log('Call data received:', data);
      if (data?.offer && !peerConnection.current!.currentRemoteDescription) {
        try {
          await peerConnection.current!.setRemoteDescription(new RTCSessionDescription(data.offer));
          console.log('Remote description set');

          // Process queued candidates after remote description is set
          while (iceCandidatesQueue.current.length) {
            const candidate = iceCandidatesQueue.current.shift()!;
            await peerConnection.current!.addIceCandidate(candidate)
              .catch(e => console.error('Error adding queued ICE candidate:', e));
          }
          
          const answer = await peerConnection.current!.createAnswer();
          console.log('Answer created:', answer);
          await peerConnection.current!.setLocalDescription(answer);
          console.log('Local description set');
          await setDoc(doc(db, `calls/${callerId}`), { answer: { type: answer.type, sdp: answer.sdp } }, { merge: true });
          console.log('Answer sent to caller');
        } catch (error) {
          console.error('Error joining call:', error);
        }
      }
    });

    setIncomingCall(null);
  };

  const listenForIncomingCalls = () => {
    console.log('Listening for incoming calls');
    onSnapshot(doc(db, `calls/${user.uid}`), (snapshot) => {
      const data = snapshot.data();
      console.log('Received call data:', data);
      if (data?.offer && data?.callerId) {
        console.log('Incoming call from:', data.callerId);
        setIncomingCall(data.callerId);
      }
    });
  };

  const endCall = () => {
    console.log('Ending call');
    if (peerConnection.current) {
      peerConnection.current.close();
    }
    setRemoteStream(null);
    setConnectionState('disconnected');
    deleteDoc(doc(db, `calls/${user.uid}`));
  };

  const rejectCall = () => {
    console.log('Rejecting call');
    setIncomingCall(null);
    deleteDoc(doc(db, `calls/${user.uid}`));
  };

  return (
    <div>
      <h2>Video Chat</h2>
      <p>Connection State: {connectionState}</p>
      <div>
        {localStream && (
          <video autoPlay playsInline muted ref={(ref) => {
            if (ref) ref.srcObject = localStream;
          }} style={{ width: '300px', height: '225px' }} />
        )}
        {remoteStream && (
          <video autoPlay playsInline ref={(ref) => {
            if (ref) ref.srcObject = remoteStream;
          }} style={{ width: '300px', height: '225px' }} />
        )}
      </div>
      {incomingCall && (
        <div>
          <p>Incoming call from {incomingCall}</p>
          <button onClick={() => joinCall(incomingCall)}>Accept</button>
          <button onClick={rejectCall}>Reject</button>
        </div>
      )}
      <button onClick={endCall}>End Call</button>
      <UserList onUserSelect={startCall} currentUserId={user.uid} />
    </div>
  );
};

export default VideoChatScreen;